import {
    Box,
    Flex,
    VStack,
    Text,
    Button,
    Icon,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverBody,
    Spinner,
} from '@chakra-ui/react';
import { TbCalendarUp, TbCategory, TbTag, TbTags, TbBox, TbChevronLeft, TbSortAscending, TbSortDescending, TbCalendar } from 'react-icons/tb';
import { IconType } from 'react-icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { InventoryAdditionalFilters, InventoryBin, InventoryPartType, InventoryTag } from '../../types/inventory';
import DatePicker from '../DatePicker';
import {
    formatInventoryPartTypeLabel,
    INVENTORY_CATEGORY_OPTIONS,
    INVENTORY_PART_TYPE_OPTIONS,
} from '../../utils/inventoryConstants';
import { useSearchInventoryTags } from '../../hooks/inventory/useSearchInventoryTags';
import { useSearchInventoryBins } from '../../hooks/inventory/useSearchInventoryBins';
import { AddFilterMenu, ClearFiltersButton, FilterBar, FilterChip } from '../../ui';
import {
    CheckboxMenuRow,
    CheckboxMenuSearch,
    MenuRow,
    MenuFooterActions,
    checkboxMenuColors,
    checkboxMenuPopoverContentSx,
    checkboxMenuTriggerMetrics,
} from '../../ui/primitives/CheckboxMenu';

export interface InventoryAdvancedFiltersProps {
    filters: InventoryAdditionalFilters;
    onFiltersChange: (filters: InventoryAdditionalFilters) => void;
    /** Current feed location scope; used to search location-scoped bins. */
    locationIds: Array<string> | 'all';
}

type FilterType = 'dateAdded' | 'category' | 'partType' | 'tags' | 'bins';
type OpenState = 'addFilter' | FilterType | null;

const FILTER_CATEGORIES: { key: FilterType; label: string; icon: IconType }[] = [
    { key: 'dateAdded', label: 'Date Added', icon: TbCalendarUp },
    { key: 'category', label: 'Category', icon: TbCategory },
    { key: 'partType', label: 'Part Type', icon: TbTag },
    { key: 'bins', label: 'Bins', icon: TbBox },
    { key: 'tags', label: 'Tags', icon: TbTags },
];

const InventoryAdvancedFilters = ({
    filters,
    onFiltersChange,
    locationIds,
}: InventoryAdvancedFiltersProps) => {
    const [openState, setOpenState] = useState<OpenState>(null);
    const [addFilterView, setAddFilterView] = useState<FilterType | null>(null);
    const [tempFilters, setTempFilters] = useState<InventoryAdditionalFilters>(filters);
    const [filterSearch, setFilterSearch] = useState('');
    const [tagSearch, setTagSearch] = useState('');
    const [debouncedTagSearch, setDebouncedTagSearch] = useState('');
    const [tagLabelById, setTagLabelById] = useState<Record<string, string>>({});
    const [binSearch, setBinSearch] = useState('');
    const [debouncedBinSearch, setDebouncedBinSearch] = useState('');
    const [binLabelById, setBinLabelById] = useState<Record<string, string>>({});
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedTagSearch(tagSearch.trim()), 250);
        return () => clearTimeout(timer);
    }, [tagSearch]);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedBinSearch(binSearch.trim()), 250);
        return () => clearTimeout(timer);
    }, [binSearch]);

    const activeEditorKey: FilterType | null =
        openState === 'addFilter'
            ? addFilterView
            : openState !== null
              ? openState
              : null;

    const tagsFilterOpen = activeEditorKey === 'tags';
    const { data: tagsData, isFetching: isTagsLoading } = useSearchInventoryTags(
        debouncedTagSearch,
        30,
        tagsFilterOpen,
    );

    const binsFilterOpen = activeEditorKey === 'bins';
    const { data: binsData, isFetching: isBinsLoading } = useSearchInventoryBins(
        locationIds,
        debouncedBinSearch,
        30,
        binsFilterOpen,
    );

    useEffect(() => {
        if (!tagsData?.tags) return;
        setTagLabelById((prev) => {
            const next = { ...prev };
            for (const t of tagsData.tags) {
                next[t._id] = t.name;
            }
            return next;
        });
    }, [tagsData?.tags]);

    useEffect(() => {
        if (!binsData?.bins) return;
        setBinLabelById((prev) => {
            const next = { ...prev };
            for (const b of binsData.bins) {
                next[b._id] = b.name;
            }
            return next;
        });
    }, [binsData?.bins]);

    const closePopover = () => {
        setOpenState(null);
        setAddFilterView(null);
        setFilterSearch('');
        setTagSearch('');
        setBinSearch('');
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!openState) return;
            const target = event.target as HTMLElement;
            if (containerRef.current?.contains(target)) return;
            if (target.closest('[data-filter-popover]')) return;
            closePopover();
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openState]);

    const togglePopover = (target: OpenState) => {
        if (openState === target) {
            closePopover();
        } else {
            setTempFilters(filters);
            setOpenState(target);
            setAddFilterView(null);
            setFilterSearch('');
            setTagSearch('');
            setBinSearch('');
        }
    };

    const applyFilters = () => {
        onFiltersChange(tempFilters);
        closePopover();
    };

    const clearFilter = (key: FilterType) => {
        const next = { ...filters };
        if (key === 'dateAdded') {
            delete next.dateAddedSort;
            delete next.dateAddedRange;
        } else if (key === 'category') {
            delete next.category;
        } else if (key === 'partType') {
            delete next.partType;
        } else if (key === 'tags') {
            delete next.tagIds;
        } else if (key === 'bins') {
            delete next.binIds;
        }
        onFiltersChange(next);
    };

    const clearAllFilters = () => {
        onFiltersChange({});
        closePopover();
    };

    const filteredCategories = FILTER_CATEGORIES.filter((c) =>
        c.label.toLowerCase().includes(filterSearch.toLowerCase()),
    );

    const selectedTagIds = tempFilters.tagIds ?? [];
    const selectedBinIds = tempFilters.binIds ?? [];
    const selectedCategories = tempFilters.category ?? [];
    const selectedPartTypes = tempFilters.partType ?? [];

    const toggleTag = (tag: InventoryTag) => {
        const current = tempFilters.tagIds ?? [];
        const exists = current.includes(tag._id);
        const nextIds = exists
            ? current.filter((id) => id !== tag._id)
            : [...current, tag._id];
        setTagLabelById((prev) => ({ ...prev, [tag._id]: tag.name }));
        setTempFilters({
            ...tempFilters,
            tagIds: nextIds.length > 0 ? nextIds : undefined,
        });
    };

    const toggleBin = (bin: InventoryBin) => {
        const current = tempFilters.binIds ?? [];
        const exists = current.includes(bin._id);
        const nextIds = exists
            ? current.filter((id) => id !== bin._id)
            : [...current, bin._id];
        setBinLabelById((prev) => ({ ...prev, [bin._id]: bin.name }));
        setTempFilters({
            ...tempFilters,
            binIds: nextIds.length > 0 ? nextIds : undefined,
        });
    };

    const toggleCategory = (value: (typeof INVENTORY_CATEGORY_OPTIONS)[number]['value']) => {
        const current = tempFilters.category ?? [];
        const exists = current.includes(value);
        const next = exists ? current.filter((c) => c !== value) : [...current, value];
        setTempFilters({
            ...tempFilters,
            category: next.length > 0 ? next : undefined,
        });
    };

    const togglePartType = (value: InventoryPartType) => {
        const current = tempFilters.partType ?? [];
        const exists = current.includes(value);
        const next = exists ? current.filter((t) => t !== value) : [...current, value];
        setTempFilters({
            ...tempFilters,
            partType: next.length > 0 ? next : undefined,
        });
    };

    const tagChipLabels = useMemo(() => {
        return (filters.tagIds ?? []).map((id) => tagLabelById[id] ?? id);
    }, [filters.tagIds, tagLabelById]);

    const binChipLabels = useMemo(() => {
        return (filters.binIds ?? []).map((id) => binLabelById[id] ?? id);
    }, [filters.binIds, binLabelById]);

    const categoryChipLabels = useMemo(() => {
        return (filters.category ?? []).map(
            (value) =>
                INVENTORY_CATEGORY_OPTIONS.find((c) => c.value === value)?.label ?? value,
        );
    }, [filters.category]);

    const isFilterActive = (key: FilterType) => {
        if (key === 'dateAdded') return filters.dateAddedSort != null;
        if (key === 'category') return (filters.category?.length ?? 0) > 0;
        if (key === 'partType') return (filters.partType?.length ?? 0) > 0;
        if (key === 'bins') return (filters.binIds?.length ?? 0) > 0;
        return (filters.tagIds?.length ?? 0) > 0;
    };

    const formatMultiChip = (prefix: string, labels: string[]) =>
        `${prefix}: ${
            labels.length <= 2 ? labels.join(', ') : `${labels.length} selected`
        }`;

    const getChipLabel = (key: FilterType) => {
        if (key === 'partType') {
            return formatMultiChip(
                'Type',
                (filters.partType ?? []).map(formatInventoryPartTypeLabel),
            );
        }
        if (key === 'category') return formatMultiChip('Category', categoryChipLabels);
        if (key === 'bins') return formatMultiChip('Bins', binChipLabels);
        if (key === 'dateAdded')
            return `Date: ${
                filters.dateAddedSort === 'range'
                    ? 'Range'
                    : filters.dateAddedSort === 'asc'
                      ? 'Oldest'
                      : 'Newest'
            }`;
        return formatMultiChip('Tags', tagChipLabels);
    };

    /** Value preview on a drill-in row — the chip label without its category prefix. */
    const getCategorySummary = (key: FilterType): string | undefined => {
        if (!isFilterActive(key)) return undefined;
        const label = getChipLabel(key);
        const separator = label.indexOf(': ');
        return separator === -1 ? label : label.slice(separator + 2);
    };

    const activeFilters = FILTER_CATEGORIES.filter((c) => isFilterActive(c.key));

    const FilterOption = ({
        label,
        isSelected,
        onClick,
        icon,
    }: {
        label: string;
        isSelected: boolean;
        onClick: () => void;
        icon?: IconType;
    }) => (
        <MenuRow
            kind="single"
            isSelected={isSelected}
            onClick={onClick}
            icon={icon ? <Icon as={icon as React.ElementType} boxSize={4} /> : undefined}
        >
            {label}
        </MenuRow>
    );

    const renderFilterEditor = (key: FilterType) => {
        if (key === 'dateAdded') {
            return (
                <VStack align="stretch" spacing={0} p="6px">
                    <FilterOption
                        label="Oldest first"
                        icon={TbSortAscending}
                        isSelected={tempFilters.dateAddedSort === 'asc'}
                        onClick={() =>
                            setTempFilters({
                                ...tempFilters,
                                dateAddedSort: 'asc',
                                dateAddedRange: undefined,
                            })
                        }
                    />
                    <FilterOption
                        label="Newest first"
                        icon={TbSortDescending}
                        isSelected={tempFilters.dateAddedSort === 'desc'}
                        onClick={() =>
                            setTempFilters({
                                ...tempFilters,
                                dateAddedSort: 'desc',
                                dateAddedRange: undefined,
                            })
                        }
                    />
                    <FilterOption
                        label="Date Range"
                        icon={TbCalendar}
                        isSelected={tempFilters.dateAddedSort === 'range'}
                        onClick={() =>
                            setTempFilters({
                                ...tempFilters,
                                dateAddedSort: 'range',
                            })
                        }
                    />
                    {tempFilters.dateAddedSort === 'range' && (
                        <Box px={3} py={2}>
                            <VStack spacing={2} align="stretch">
                                <Box>
                                    <Text fontSize="xs" fontWeight="medium" color="gray.500" mb={1}>
                                        START DATE
                                    </Text>
                                    <DatePicker
                                        value={tempFilters.dateAddedRange?.start ?? new Date()}
                                        onChange={(date) =>
                                            setTempFilters({
                                                ...tempFilters,
                                                dateAddedRange: {
                                                    start: date,
                                                    end: tempFilters.dateAddedRange?.end ?? date,
                                                },
                                            })
                                        }
                                        placeholder="Start"
                                    />
                                </Box>
                                <Box>
                                    <Text fontSize="xs" fontWeight="medium" color="gray.500" mb={1}>
                                        END DATE
                                    </Text>
                                    <DatePicker
                                        value={tempFilters.dateAddedRange?.end ?? new Date()}
                                        onChange={(date) =>
                                            setTempFilters({
                                                ...tempFilters,
                                                dateAddedRange: {
                                                    start: tempFilters.dateAddedRange?.start ?? date,
                                                    end: date,
                                                },
                                            })
                                        }
                                        placeholder="End"
                                    />
                                </Box>
                            </VStack>
                        </Box>
                    )}
                </VStack>
            );
        }

        if (key === 'category') {
            return (
                <VStack align="stretch" spacing={0} p="6px">
                    {INVENTORY_CATEGORY_OPTIONS.map((opt) => (
                        <CheckboxMenuRow
                            key={opt.value}
                            isSelected={selectedCategories.includes(opt.value)}
                            onClick={() => toggleCategory(opt.value)}
                        >
                            {opt.label}
                        </CheckboxMenuRow>
                    ))}
                </VStack>
            );
        }

        if (key === 'partType') {
            return (
                <VStack align="stretch" spacing={0} p="6px">
                    {INVENTORY_PART_TYPE_OPTIONS.map((opt) => (
                        <CheckboxMenuRow
                            key={opt.value}
                            isSelected={selectedPartTypes.includes(opt.value)}
                            onClick={() => togglePartType(opt.value)}
                        >
                            {opt.label}
                        </CheckboxMenuRow>
                    ))}
                </VStack>
            );
        }

        if (key === 'tags') {
            return (
                <>
                    <CheckboxMenuSearch
                        value={tagSearch}
                        onChange={setTagSearch}
                        placeholder="Search tags…"
                    />
                    {isTagsLoading && (
                        <Flex justify="center" py={2}>
                            <Spinner size="sm" color={checkboxMenuColors.brand} />
                        </Flex>
                    )}
                    <VStack align="stretch" spacing={0} p="6px" maxH="200px" overflowY="auto">
                        {(tagsData?.tags ?? []).map((tag) => (
                            <CheckboxMenuRow
                                key={tag._id}
                                isSelected={selectedTagIds.includes(tag._id)}
                                onClick={() => toggleTag(tag)}
                            >
                                {tag.name}
                            </CheckboxMenuRow>
                        ))}
                        {!isTagsLoading && (tagsData?.tags?.length ?? 0) === 0 && (
                            <Text
                                fontSize={checkboxMenuTriggerMetrics.fontSize}
                                color={checkboxMenuColors.muted}
                                px="10px"
                                py="8px"
                            >
                                No tags found
                            </Text>
                        )}
                    </VStack>
                </>
            );
        }

        if (key === 'bins') {
            return (
                <>
                    <CheckboxMenuSearch
                        value={binSearch}
                        onChange={setBinSearch}
                        placeholder="Search bins…"
                    />
                    {isBinsLoading && (
                        <Flex justify="center" py={2}>
                            <Spinner size="sm" color={checkboxMenuColors.brand} />
                        </Flex>
                    )}
                    <VStack align="stretch" spacing={0} p="6px" maxH="200px" overflowY="auto">
                        {(binsData?.bins ?? []).map((bin) => (
                            <CheckboxMenuRow
                                key={bin._id}
                                isSelected={selectedBinIds.includes(bin._id)}
                                onClick={() => toggleBin(bin)}
                            >
                                {bin.name}
                            </CheckboxMenuRow>
                        ))}
                        {!isBinsLoading && (binsData?.bins?.length ?? 0) === 0 && (
                            <Text
                                fontSize={checkboxMenuTriggerMetrics.fontSize}
                                color={checkboxMenuColors.muted}
                                px="10px"
                                py="8px"
                            >
                                No bins found
                            </Text>
                        )}
                    </VStack>
                </>
            );
        }

        return null;
    };

    const renderFooter = (showReset: boolean = true) => (
        <MenuFooterActions
            secondaryLabel={showReset ? 'Reset' : 'Cancel'}
            onSecondary={showReset ? () => setTempFilters({}) : closePopover}
            onApply={applyFilters}
        />
    );

    return (
        <Box ref={containerRef}>
            <FilterBar>
                {activeFilters.map((cat) => (
                    <Popover
                        key={cat.key}
                        isOpen={openState === cat.key}
                        placement="bottom-start"
                        closeOnBlur={false}
                    >
                        <PopoverTrigger>
                            <FilterChip
                                icon={<Icon as={cat.icon as React.ElementType} boxSize={4} />}
                                label={getChipLabel(cat.key)}
                                onRemove={() => clearFilter(cat.key)}
                                onClick={() => togglePopover(cat.key)}
                            />
                        </PopoverTrigger>
                        <PopoverContent
                            w="280px"
                            sx={checkboxMenuPopoverContentSx}
                            data-filter-popover
                        >
                            <PopoverBody p={0}>
                                {renderFilterEditor(cat.key)}
                                {renderFooter(false)}
                            </PopoverBody>
                        </PopoverContent>
                    </Popover>
                ))}

                <AddFilterMenu
                    isOpen={openState === 'addFilter'}
                    onToggle={() => togglePopover('addFilter')}
                    width={addFilterView ? '280px' : '220px'}
                    contentProps={{ 'data-filter-popover': '' }}
                >
                            {addFilterView === null ? (
                                <>
                                    <CheckboxMenuSearch
                                        value={filterSearch}
                                        onChange={setFilterSearch}
                                        placeholder="Search filters..."
                                    />
                                    <VStack align="stretch" spacing={0} p="6px">
                                        {filteredCategories.map((cat) => (
                                            <MenuRow
                                                key={cat.key}
                                                kind="drillIn"
                                                summary={getCategorySummary(cat.key)}
                                                icon={
                                                    <Icon as={cat.icon as React.ElementType} boxSize={4} />
                                                }
                                                onClick={() => setAddFilterView(cat.key)}
                                            >
                                                {cat.label}
                                            </MenuRow>
                                        ))}
                                    </VStack>
                                    {renderFooter(true)}
                                </>
                            ) : (
                                <>
                                    <Box
                                        px={2}
                                        py={2}
                                        borderBottom="1px solid"
                                        borderColor="gray.100"
                                    >
                                        <Button
                                            size="xs"
                                            variant="ghost"
                                            onClick={() => {
                                                setAddFilterView(null);
                                                setTagSearch('');
                                            }}
                                            leftIcon={
                                                <Icon as={TbChevronLeft as React.ElementType} />
                                            }
                                            color="gray.600"
                                            fontWeight="medium"
                                        >
                                            {
                                                FILTER_CATEGORIES.find((c) => c.key === addFilterView)
                                                    ?.label
                                            }
                                        </Button>
                                    </Box>
                                    <Box p={0}>{renderFilterEditor(addFilterView)}</Box>
                                    {renderFooter(true)}
                                </>
                            )}
                </AddFilterMenu>
                {activeFilters.length > 0 && (
                    <ClearFiltersButton onClick={clearAllFilters} />
                )}
            </FilterBar>
        </Box>
    );
};

export default InventoryAdvancedFilters;
